import { Icon } from '@iconify/react';
import { useState } from 'react';
import { t } from '../i18n';
import { type PushState, enablePush, pushState } from '../push';

// The whole task-dispatch model assumes a task reaches the person's pocket.
// Push was wired end to end — VAPID keys, subscription, dispatcher — but the
// only way to switch it on was a toggle buried in Profile, which nobody ever
// opens. So in practice the driver's phone stayed silent.
//
// One tap, on the screen they actually live on, and dismissible so it never
// becomes nagware. Shown only when the browser supports push and has never
// been asked: once granted or denied, this disappears for good.

const DISMISS_KEY = 'fm.pushPromptDismissed';

export function EnableNotifications() {
  const [state, setState] = useState<PushState>(() => pushState());
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [busy, setBusy] = useState(false);

  if (dismissed || state !== 'default') return null;

  async function turnOn() {
    setBusy(true);
    try {
      setState(await enablePush());
    } catch {
      // Server VAPID missing or the browser refused — either way there is
      // nothing useful to say here, and the card simply stays put.
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  // A row, not a slab. This used to be a full card with a heading, a
  // paragraph and two buttons, sitting above the queue — the largest thing on
  // a screen whose whole point is that the queue is the first thing you see.
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-task/10 px-4 py-3">
      <Icon icon="ph:bell-ringing-fill" className="shrink-0 text-lg text-task" />
      <p className="min-w-0 flex-1 text-xs font-semibold leading-snug text-task">
        {t('Otherwise you only find out about new work when you open the app.')}
      </p>
      <button
        type="button"
        onClick={turnOn}
        disabled={busy}
        className="shrink-0 rounded-xl bg-task px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-transform active:scale-95"
      >
        {busy ? '…' : t('Turn on')}
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('Not now')}
        className="shrink-0 text-task/60"
      >
        <Icon icon="ph:x-bold" className="text-sm" />
      </button>
    </div>
  );
}
